import { alpha, Box, Button, CircularProgress, darken, useTheme } from '@mui/material';
import { useCustomizations } from 'src/components/utils/theme/customizations';

import type { JSX } from 'react';

export function PrintButton(props: { text: string; onPrint: () => void }) {
    const customizations = useCustomizations();
    return (
        <Button
            variant={customizations.primaryButtonStyle}
            color="primary"
            onClick={props.onPrint}
            className="no-print"
        >
            {props.text}
        </Button>
    );
}

export function ContinueShoppingButton(props: {
    text: string;
    onClick?: () => void;
    href?: string;
}): JSX.Element {
    const customizations = useCustomizations();
    const theme = useTheme();

    return (
        <Button
            variant="outlined"
            sx={{
                color: customizations.bookingWidgetPrimaryColor,
                backgroundColor: customizations.bookingWidgetColor,
                border: `1px solid ${customizations.bookingWidgetPrimaryColor}`,
                marginBottom: theme.spacing(1),
                '&:hover': {
                    backgroundColor:
                        customizations.primaryButtonStyle === 'contained'
                            ? darken(theme.palette.primary.main, 0.2)
                            : alpha(theme.palette.common.black, 0.3),
                },
            }}
            href={props.href}
            onClick={props.onClick}
        >
            {props.text}
        </Button>
    );
}

export function NextButton(props: { text: string; onClick?: () => void }): JSX.Element {
    const customizations = useCustomizations();
    const theme = useTheme();

    return (
        <Button
            color="button"
            sx={{
                backgroundColor:
                    customizations.primaryButtonStyle === 'contained'
                        ? `${customizations.bookingWidgetPrimaryColor} !important`
                        : 'transparent !important',
                color:
                    customizations.primaryButtonStyle === 'contained'
                        ? `${customizations.bookingWidgetColor} !important`
                        : `${customizations.bookingWidgetPrimaryColor} !important`,
                marginBottom: theme.spacing(1),

                borderColor:
                    customizations.primaryButtonStyle === 'contained'
                        ? 'transparent'
                        : customizations.bookingWidgetPrimaryColor,
                '&:hover': {
                    backgroundColor:
                        customizations.primaryButtonStyle === 'contained'
                            ? `${darken(customizations.bookingWidgetPrimaryColor, 0.2)} !important`
                            : `${alpha(theme.palette.common.black, 0.3)} !important`,
                    color:
                        customizations.primaryButtonStyle === 'contained'
                            ? `${darken(customizations.bookingWidgetColor, 0.2)} !important`
                            : undefined,
                },
                '&:focus': {
                    boxShadow: `0 0 0 2px ${customizations.bookingWidgetColor}, \
                                                    0 0 0 3px ${customizations.bookingWidgetPrimaryColor} !important`,
                },
            }}
            variant={customizations.primaryButtonStyle}
            onClick={props.onClick}
        >
            {props.text}
        </Button>
    );
}

export function ContinueRebookingButton(props: { href: string; text: string }): JSX.Element {
    const customizations = useCustomizations();
    const theme = useTheme();

    return (
        <Button
            sx={{ mt: theme.spacing(2) }}
            color="warning"
            variant={customizations.primaryButtonStyle}
            href={props.href}
        >
            {props.text}
        </Button>
    );
}

export function PayWithInvoiceButton(props: {
    disabled: boolean;
    text: string;
    onClick?: () => void;
}): JSX.Element {
    const customizations = useCustomizations();

    return (
        <Button
            color="primary"
            variant={customizations.primaryButtonStyle}
            disabled={props.disabled}
            onClick={props.onClick}
        >
            {props.text}
        </Button>
    );
}

export function GoPayButton(props: {
    disabled: boolean;
    text: string;
    onClick?: () => void;
    minWidth?: string;
    loading?: boolean;
}): JSX.Element {
    const customizations = useCustomizations();

    return (
        <Button
            sx={{ minWidth: props.minWidth }}
            color="primary"
            variant={customizations.primaryButtonStyle}
            disabled={props.disabled}
            onClick={props.onClick}
        >
            {goPayButtonDisplay(props.text, props.loading)}
        </Button>
    );
}

function goPayButtonDisplay(text: string, isMakingReservation?: boolean): JSX.Element {
    if (isMakingReservation) {
        return (
            <Box>
                <CircularProgress
                    color="primary"
                    sx={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        marginLeft: 'auto',
                        marginRight: 'auto',
                    }}
                />
                <span>{text}</span>
            </Box>
        );
    } else {
        return (
            <Box>
                <span>{text}</span>
            </Box>
        );
    }
}
